/**
 * InviteDialog Component (ORCH-336, ORCH-21 §12; PRD User Manager UM-10)
 *
 * Purpose: Invite one or more people who all get the same access: a row per
 *          person (first name, last name, email), then the roles, Company and
 *          security attributes they will all get (ORCH-410 §3.1). Company is
 *          required here: the admin assigns it for the whole batch (Q8).
 * Logic: The membership fields are the same MembershipForm the user page uses,
 *        scoped to `user.create`. Nothing is pre-selected, and Send N Invites
 *        stays disabled until every row and the membership are complete. Each
 *        row is checked on its own, including addresses repeated in the batch.
 *        The server decides the rest: a scoped admin must give attributes inside
 *        their own scope, roles must be assignable, and an existing member of
 *        the tenant refuses the whole batch (flagged on its row). Only after
 *        sending does the inviter learn that an address already had an account,
 *        named by what they typed (§3.1.1).
 * Dependencies: MembershipForm, userManagementService
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '../common';
import type { RoleOption } from '../common';
import { PermissionKey } from '../../constants/permissionKeys';
import userManagementService, {
  userError,
  userErrorMessage,
} from '../../services/userManagementService';
import { Invitee, InviteResult } from '../../types/userManagement';
import { validateEmail } from '../../utils/validation';
import { asList } from '../../utils/listValues';
import { fieldGridSx } from '../form-layout/fieldGrid';
import MembershipForm from './MembershipForm';
import { cleanMetadata, MembershipDraft, COMPANY_FIELD } from './membership';
import { SecondaryButton } from '../common/ActionButtons';

export interface InviteDialogProps {
  open: boolean;
  tenantId: string;
  /** Sent only when a system admin invites into a tenant other than the active one. */
  targetTenantId?: string;
  roles: RoleOption[];
  attributeFieldIds: string[];
  canAssignRoles: boolean;
  onClose: () => void;
  /** Called after a successful send with the summary to show. */
  onInvited: (summary: string) => void;
}

const EMPTY_PERSON: Invitee = { email: '', first_name: '', last_name: '' };
const EMPTY_DRAFT: MembershipDraft = { roleIds: [], metadata: {} };

type PersonField = keyof Invitee;
type RowErrors = Partial<Record<PersonField, string>>;

const emailKey = (email: string) => email.trim().toLowerCase();

/** What is wrong with each row, before anything is sent. */
export const rowErrors = (people: Invitee[]): RowErrors[] => {
  const seen = new Set<string>();
  return people.map((person) => {
    const errors: RowErrors = {};
    if (!person.first_name.trim()) errors.first_name = 'First Name is required';
    if (!person.last_name.trim()) errors.last_name = 'Last Name is required';
    const key = emailKey(person.email);
    if (!key) errors.email = 'Email Address is required';
    else if (!validateEmail(key)) errors.email = 'Enter a valid email address';
    else if (seen.has(key)) errors.email = 'Already in this list';
    if (key) seen.add(key);
    return errors;
  });
};

const fullName = (person: Invitee) => `${person.first_name.trim()} ${person.last_name.trim()}`;

/** The batch result, naming existing accounts by the name the inviter typed. */
export const inviteSummary = (people: Invitee[], results: InviteResult['results']): string => {
  const count = results.length;
  const sent = `${count === 1 ? 'Invite' : `${count} invites`} sent.`;
  const existing = results
    .filter((r) => r.outcome === 'added')
    .map((r) => people.find((p) => emailKey(p.email) === emailKey(r.email)))
    .filter((p): p is Invitee => Boolean(p))
    .map((p) => `${fullName(p)} (${p.email.trim()})`);
  if (existing.length === 0) return `${sent} They show as Invited until they accept.`;
  const who = existing.join(', ');
  return existing.length === 1
    ? `${sent} ${who} already had an Orchestrate account: access added, existing name kept.`
    : `${sent} ${who} already had Orchestrate accounts: access added, existing names kept.`;
};

const InviteDialog: React.FC<InviteDialogProps> = ({
  open,
  tenantId,
  targetTenantId,
  roles,
  attributeFieldIds,
  canAssignRoles,
  onClose,
  onInvited,
}) => {
  const [people, setPeople] = useState<Invitee[]>([EMPTY_PERSON]);
  const [draft, setDraft] = useState<MembershipDraft>(EMPTY_DRAFT);
  // A fresh object per opening resets the membership form
  const [initial, setInitial] = useState<MembershipDraft>(EMPTY_DRAFT);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fields the inviter has left, so errors show once touched, not while typing
  const [touched, setTouched] = useState<Set<string>>(new Set());
  // Refusals the server put on a row (ALREADY_MEMBER), keyed by row index
  const [serverErrors, setServerErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (open) {
      setPeople([EMPTY_PERSON]);
      setDraft(EMPTY_DRAFT);
      setInitial({ roleIds: [], metadata: {} });
      setError(null);
      setTouched(new Set());
      setServerErrors({});
    }
  }, [open]);

  const setPerson = (index: number, patch: Partial<Invitee>) => {
    setPeople((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    if (patch.email !== undefined) {
      setServerErrors(({ [index]: _changed, ...rest }) => rest);
    }
  };

  const removePerson = (index: number) => {
    setPeople((rows) => rows.filter((_, i) => i !== index));
    // Row indexes shift down past the removed one
    const shift = <T,>(byRow: Record<number, T>) =>
      Object.fromEntries(
        Object.entries(byRow)
          .filter(([i]) => Number(i) !== index)
          .map(([i, v]) => [Number(i) > index ? Number(i) - 1 : Number(i), v])
      ) as Record<number, T>;
    setServerErrors(shift);
    setTouched(
      (keys) =>
        new Set(
          Array.from(keys)
            .map((key) => key.split(':'))
            .filter(([i]) => Number(i) !== index)
            .map(([i, f]) => `${Number(i) > index ? Number(i) - 1 : i}:${f}`)
        )
    );
  };

  const touch = (index: number, field: PersonField) =>
    setTouched((keys) => new Set(keys).add(`${index}:${field}`));

  const errors = useMemo(() => rowErrors(people), [people]);

  const fieldError = (index: number, field: PersonField): string | undefined => {
    if (field === 'email' && serverErrors[index]) return serverErrors[index];
    const message = errors[index]?.[field];
    // A duplicate is worth saying straight away; the rest wait for the field to be left
    if (message === 'Already in this list') return message;
    return touched.has(`${index}:${field}`) ? message : undefined;
  };

  const ready = useMemo(
    () =>
      errors.every((row) => Object.keys(row).length === 0) &&
      Object.keys(serverErrors).length === 0 &&
      draft.roleIds.length > 0 &&
      asList(draft.metadata[COMPANY_FIELD]).length > 0,
    [errors, serverErrors, draft]
  );

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await userManagementService.invite({
        invitees: people.map((p) => ({
          email: p.email.trim(),
          first_name: p.first_name.trim(),
          last_name: p.last_name.trim(),
        })),
        role_ids: draft.roleIds,
        metadata: cleanMetadata(draft.metadata),
        ...(targetTenantId ? { tenant_id: targetTenantId } : {}),
      });
      onInvited(inviteSummary(people, result.results));
    } catch (e) {
      const refusal = userError(e);
      const byRow = (refusal.details as { invitees?: Record<string, string[]> } | undefined)
        ?.invitees;
      if (byRow && Object.keys(byRow).length > 0) {
        setServerErrors(
          Object.fromEntries(Object.entries(byRow).map(([i, messages]) => [Number(i), messages[0]]))
        );
      }
      setError(userErrorMessage(e, 'The invite could not be sent.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      data-testid="invite-dialog"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Invite People
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} aria-label="Close" disabled={sending} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        {error && (
          <Alert severity="error" data-testid="invite-error">
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {people.map((person, index) => (
            <Box
              key={index}
              data-testid={`invite-person-${index}`}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'start' }}
            >
              <Box sx={{ ...fieldGridSx(3), flex: 1 }}>
                <TextField
                  label="First Name"
                  required
                  size="small"
                  value={person.first_name}
                  onChange={(e) => setPerson(index, { first_name: e.target.value })}
                  onBlur={() => touch(index, 'first_name')}
                  error={Boolean(fieldError(index, 'first_name'))}
                  helperText={fieldError(index, 'first_name')}
                  inputProps={{ 'data-testid': `invite-first-name-${index}` }}
                />
                <TextField
                  label="Last Name"
                  required
                  size="small"
                  value={person.last_name}
                  onChange={(e) => setPerson(index, { last_name: e.target.value })}
                  onBlur={() => touch(index, 'last_name')}
                  error={Boolean(fieldError(index, 'last_name'))}
                  helperText={fieldError(index, 'last_name')}
                  inputProps={{ 'data-testid': `invite-last-name-${index}` }}
                />
                <TextField
                  label="Email Address"
                  type="email"
                  required
                  size="small"
                  value={person.email}
                  onChange={(e) => setPerson(index, { email: e.target.value })}
                  onBlur={() => touch(index, 'email')}
                  error={Boolean(fieldError(index, 'email'))}
                  helperText={fieldError(index, 'email')}
                  inputProps={{ 'data-testid': `invite-email-${index}` }}
                />
              </Box>
              {people.length > 1 ? (
                <Tooltip title="Remove This Person">
                  <IconButton
                    size="small"
                    aria-label="Remove this person"
                    onClick={() => removePerson(index)}
                    data-testid={`invite-remove-person-${index}`}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Box sx={{ width: 34 }} />
              )}
            </Box>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setPeople((rows) => [...rows, EMPTY_PERSON])}
              data-testid="invite-add-person"
            >
              Add Another Person
            </Button>
          </Box>
        </Box>

        <MembershipForm
          tenantId={tenantId}
          roles={roles}
          initial={initial}
          value={draft}
          onChange={setDraft}
          permission={PermissionKey.USER_CREATE}
          canAssignRoles={canAssignRoles}
          attributeFieldIds={attributeFieldIds}
          warnNoRoles={false}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton onClick={onClose} disabled={sending}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          variant="contained"
          loading={sending}
          disabled={!ready}
          onClick={handleSend}
          data-testid="invite-send"
        >
          {people.length === 1 ? 'Send 1 Invite' : `Send ${people.length} Invites`}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default InviteDialog;
